import { useCallback, useEffect, useRef, useState } from 'react';
import { type DialerHost, type HostDialerState, NO_HOST_DIALER } from '../../embed/dialerState';
import { useComms } from './context';
import { dialerStatusLine, dialNumber } from './dialerBridge';
import { DIAL_WAIT_MS, hasRingCentralLine, type RcDialer } from './useRcDialer';

/**
 * The RingCentral dialer when the DASHBOARD holds it, wearing the same shape
 * as the one this module mounts for itself.
 *
 * Inside the dispatch dashboard the widget cannot live here. This module is
 * framed by the communication route and disposed the moment the agent opens a
 * reservation, and a second copy of the widget beside the shell's would be two
 * web phones on one RingCentral user, forking every ring. The shell keeps one
 * frame on every page instead, and the module drives that.
 *
 * What is mirrored is what OFFERING a call needs — signed in, registered,
 * busy — and the one way to bring the panel on screen. What is not mirrored
 * is the ring: answering and ignoring are the dashboard's own card's buttons,
 * which it raises on every page including this one, so `ringFor` answers
 * null here and the module's own card never draws for a hosted phone.
 *
 * The shell frames its dialer for every agent with Communications access,
 * RingCentral channel or not — so `available` is still this module's answer,
 * from its own channel list, exactly as for the frame it would have mounted.
 */
export function useHostDialer(host: DialerHost): RcDialer {
  const { inboxes, canCall } = useComms();
  const [state, setState] = useState<HostDialerState>(NO_HOST_DIALER);

  useEffect(() => host.subscribe(setState), [host]);

  /**
   * The dial waiting for the dashboard's word, if any. The dashboard relays
   * its dialer's state, and `hasCall` flipping on is the widget saying the
   * call began; a dial that does not flip it within DIAL_WAIT_MS did not
   * happen, and the promise says so — the same contract as the module's own
   * dialer, so the Call button reads one answer either way.
   */
  const pendingDial = useRef<{ resolve: (started: boolean) => void; timer: ReturnType<typeof setTimeout> } | null>(
    null,
  );
  useEffect(() => {
    if (!state.hasCall || !pendingDial.current) return;
    const pending = pendingDial.current;
    pendingDial.current = null;
    clearTimeout(pending.timer);
    pending.resolve(true);
  }, [state.hasCall]);
  useEffect(
    () => () => {
      const pending = pendingDial.current;
      if (!pending) return;
      pendingDial.current = null;
      clearTimeout(pending.timer);
      pending.resolve(false);
    },
    [],
  );

  const dial = useCallback(
    (to: string) =>
      new Promise<boolean>((resolve) => {
        const number = dialNumber(to);
        if (!number) {
          resolve(false);
          return;
        }
        if (pendingDial.current) {
          clearTimeout(pendingDial.current.timer);
          pendingDial.current.resolve(false);
        }
        host.send({ action: 'dial', to: number });
        pendingDial.current = {
          resolve,
          timer: setTimeout(() => {
            pendingDial.current = null;
            resolve(false);
          }, DIAL_WAIT_MS),
        };
      }),
    [host],
  );
  const openPanel = useCallback(() => host.send({ action: 'open' }), [host]);
  const noop = useCallback(() => {}, []);
  const ringFor = useCallback(() => null, []);

  return {
    available: hasRingCentralLine(inboxes, canCall),
    hosted: true,
    frameUrl: null,
    attachFrame: noop,
    known: state.known,
    registered: state.registered,
    loggedIn: state.loggedIn,
    hasCall: state.hasCall,
    panelOpen: state.panelOpen,
    statusLine: dialerStatusLine({
      known: state.known,
      loggedIn: state.loggedIn,
      webphone: state.registered,
      ringing: [],
      onCall: state.hasCall,
      active: null,
      activeSince: null,
    }),
    openPanel,
    // Hiding is the dashboard's title bar's job; nothing here draws a panel.
    closePanel: noop,
    ringFor,
    answer: noop,
    ignore: noop,
    dial,
    canDial: state.loggedIn,
    activeCall: state.call
      ? {
          sessionKey: state.call.sessionKey,
          peer: state.call.peer,
          name: state.call.name,
          since: state.call.since,
          conversationDisplayId: state.call.conversationDisplayId,
        }
      : null,
  };
}
